import React, { ReactNode, useEffect, useState } from "react";

import {
  AccountTreeOutlined,
  BarChartOutlined,
  ChecklistOutlined,
  DashboardOutlined,
  FolderOpenOutlined,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { ClientCharacteristicsStrip } from "./ClientCharacteristicsStrip";
import { HorizontalNavBar } from "components/HorizontalNavBar/HorizontalNavBar";

interface TabDefinition {
  value: string;
  label: string;
}

interface ClientPageShellProps {
  clientId: number;
  clientName: string;
  /** Default tab to show on mount (defaults to "overview") */
  defaultTab?: string;
  /** Optional navigation handler passed through to tab components */
  onNavigate?: (route: string, params: Record<string, string | number>) => void;
  tabs?: TabDefinition[];
  /** Tab-specific content renderer. Keys are tab values. */
  tabsContent?: Record<string, ReactNode>;
  /** Fallback when tabsContent is not provided */
  children?: ReactNode;
}

const TAB_ICON_MAP: Record<string, React.ReactElement> = {
  overview: <DashboardOutlined sx={{ fontSize: "16px" }} />,
  entities: <AccountTreeOutlined sx={{ fontSize: "16px" }} />,
  tasks: <ChecklistOutlined sx={{ fontSize: "16px" }} />,
  workspace: <FolderOpenOutlined sx={{ fontSize: "16px" }} />,
  reporting: <BarChartOutlined sx={{ fontSize: "16px" }} />,
};

const DEFAULT_TABS: TabDefinition[] = [
  { value: "overview", label: "Overview" },
  { value: "entities", label: "Entities" },
  { value: "tasks", label: "Tasks" },
  { value: "workspace", label: "Workspace" },
  { value: "reporting", label: "Reporting" },
];

export const ClientPageShell: React.FC<ClientPageShellProps> = ({
  clientId,
  clientName,
  defaultTab = "overview",
  tabs = DEFAULT_TABS,
  children,
  tabsContent
}) => {
  const [activeTab, setActiveTab] = useState<string>(() => {
    const stored = sessionStorage.getItem("clientPage_activeTab");
    return stored || defaultTab || tabs[0]?.value || "overview";
  });

  useEffect(() => {
    if (!tabs.length) {
      return;
    }

    const tabExists = tabs.some((tab) => tab.value === activeTab);
    if (tabExists) {
      return;
    }

    const fallback = tabs.find((tab) => tab.value === defaultTab)?.value ?? tabs[0].value;
    setActiveTab(fallback);
    sessionStorage.setItem("clientPage_activeTab", fallback);
  }, [activeTab, defaultTab, tabs]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    sessionStorage.setItem("clientPage_activeTab", newValue);
  };

  const navTabs = tabs.map((tab) => ({
    ...tab,
    icon: TAB_ICON_MAP[tab.value],
  }));

  return (
    <Box className="client-page-shell">
      <Box sx={{ mb: "24px" }}>
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 500,
            color: "#0a3142",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "26px",
            mb: "16px"
          }}
        >
          {clientName} Management
        </Typography>
        <HorizontalNavBar
          tabs={navTabs}
          value={activeTab}
          onChange={handleTabChange}
        />
      </Box>

      <ClientCharacteristicsStrip
        clientId={clientId}
      />

      <Box
        sx={{
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 0 }}>
          {tabsContent?.[activeTab] || children || (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
              }}
            >
              {/* Tab content for "{activeTab}" renders here. */}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
